import { getUniqueId } from "@jugl-web/utils";
import type { CustomTemplateData } from "@jugl-web/rest-api";
import type { DataInputItem } from "../components/CXAutomationApiSetupSection/components/DataInputRow";
import type { RequestRow } from "../components/CXAutomationApiSetupSection/components/RequestKeyValueRow";
import {
  CXAutomationFormState,
  HttpMethod,
  RequestTabId,
  createEmptyAutomationFormState,
} from "../types";

const SUPPORTED_METHODS: HttpMethod[] = [
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
];

const toMethod = (raw: string | undefined): HttpMethod => {
  const upper = (raw ?? "GET").toUpperCase();
  return (SUPPORTED_METHODS as string[]).includes(upper)
    ? (upper as HttpMethod)
    : "GET";
};

const toRequestRows = (entries: [string, unknown][]): RequestRow[] =>
  entries.map(([key, value]) => ({
    id: getUniqueId(),
    key,
    value: typeof value === "string" ? value : JSON.stringify(value),
  }));

const splitUrlAndQuery = (
  url: string
): { baseUrl: string; queryRows: RequestRow[] } => {
  const [baseUrl, query] = url.split("?", 2);
  if (!query) return { baseUrl, queryRows: [] };
  const params = new URLSearchParams(query);
  const queryRows: RequestRow[] = [];
  params.forEach((value, key) => {
    queryRows.push({ id: getUniqueId(), key, value });
  });
  return { baseUrl, queryRows };
};

const variablesToDataInputs = (data: CustomTemplateData): DataInputItem[] =>
  data.variables.map((variable) => ({
    id: getUniqueId(),
    name: variable.name,
    description: variable.desc,
  }));

export const deserializeCustomTemplateToAutomation = (
  data: CustomTemplateData | undefined
): CXAutomationFormState => {
  if (!data) return createEmptyAutomationFormState();

  const method = toMethod(data.method);
  const { baseUrl, queryRows } = splitUrlAndQuery(data.url ?? "");

  const requestRowsByTab: Record<RequestTabId, RequestRow[]> = {
    parameters: queryRows,
    headers: toRequestRows(Object.entries(data.headers ?? {})),
    body: toRequestRows(Object.entries(data.body ?? {})),
  };

  return {
    automationName: (data.name ?? "").replace(/_/g, " "),
    description: data.description ?? "",
    method,
    urlMarkdown: baseUrl,
    dataInputs: variablesToDataInputs(data),
    requestRowsByTab,
  };
};
